import ConfirmAction from '@/components/confirm-action';
import StatusPill, { type Tone } from '@/components/console/status-pill';
import EmptyState from '@/components/empty-state';
import PageHeader from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime, humanize } from '@/lib/format';
import { admin_provisioning_reset_path } from '@/rails/routes';
import type { Provisioning, ProvisioningStatus } from '@/types/platform';
import { router } from '@inertiajs/react';
import { Check, Circle } from 'lucide-react';

interface ProvisioningRow extends Provisioning {
  app: { id: string; name: string; slug: string };
  companyName: string;
}

interface Props {
  provisionings: ProvisioningRow[];
}

const STATUS_TONES: Record<ProvisioningStatus, Tone> = {
  pending: 'neutral',
  running: 'info',
  succeeded: 'success',
  failed: 'danger',
  halted: 'danger',
};

const Index = ({ provisionings }: Props) => {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Provisioning"
        description="App setups that are not done. A setup halts after two failures. Reset and run again resumes the same row; it never starts a second one."
      />

      <Card className="py-2">
        <CardContent className="px-2">
          {provisionings.length === 0 ? (
            <EmptyState
              className="px-4"
              title="Nothing is waiting."
              description="Every app setup finished. A setup that fails or halts shows up here."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>App</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Attempts</TableHead>
                  <TableHead className="text-right">Failures</TableHead>
                  <TableHead>Steps</TableHead>
                  <TableHead>Last error</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead>
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {provisionings.map((provisioning) => (
                  <TableRow key={provisioning.id}>
                    <TableCell className="font-medium">{provisioning.companyName}</TableCell>
                    <TableCell>
                      <div>{provisioning.app.name}</div>
                      <div className="text-muted-foreground text-xs">{provisioning.app.slug}</div>
                    </TableCell>
                    <TableCell>
                      <StatusPill tone={STATUS_TONES[provisioning.status]}>
                        {humanize(provisioning.status)}
                      </StatusPill>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {provisioning.attempts}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {provisioning.failures}
                    </TableCell>
                    <TableCell>
                      <Steps steps={provisioning.steps} />
                    </TableCell>
                    <TableCell className="max-w-xs min-w-48 whitespace-normal">
                      {provisioning.lastError ? (
                        <span className="text-sm break-words text-red-700">
                          {provisioning.lastError}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDateTime(provisioning.updatedAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <ConfirmAction
                        title={`Reset the setup for ${provisioning.app.name}?`}
                        description="Clears the failure count and the last error, then runs the same setup row again. Steps that already finished are skipped."
                        confirmLabel="Reset and run again"
                        onConfirm={() =>
                          router.post(admin_provisioning_reset_path(provisioning.id))
                        }
                      >
                        <Button variant="outline" size="sm">
                          Reset and run again
                        </Button>
                      </ConfirmAction>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const Steps = ({ steps }: { steps: Provisioning['steps'] }) => {
  return (
    <ul className="space-y-0.5 text-sm">
      {steps.map((step) => (
        <li key={step.key} className="flex items-center gap-1.5">
          {step.done ? (
            <Check className="h-3.5 w-3.5 text-green-700" aria-hidden="true" />
          ) : (
            <Circle className="text-muted-foreground h-3.5 w-3.5" aria-hidden="true" />
          )}
          <span>{step.label}</span>
          <span className="text-muted-foreground text-xs">{step.done ? 'done' : 'not done'}</span>
        </li>
      ))}
    </ul>
  );
};

export default Index;
